import React, { useRef, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useKeyboardNavigation, useAnnouncer } from '@/lib/hooks/useAccessibility'

interface ProjectFiltersProps {
  projectTypes: string[]
  selectedType: string | null
  onTypeChange: (type: string | null) => void
}

const ProjectFilters: React.FC<ProjectFiltersProps> = ({
  projectTypes,
  selectedType,
  onTypeChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const announce = useAnnouncer()

  const handleTypeChange = (type: string) => {
    const newType = type === selectedType ? null : type
    onTypeChange(newType)

    if (newType) {
      announce(`Filter applied: ${getDisplayName(newType)}`)
    } else {
      announce('Filter cleared')
    }
  }
  // Map project type values to display names
  const getDisplayName = (type: string): string => {
    const displayNames: Record<string, string> = {
      dapp: 'DApp',
      'smart-contract': 'Smart Contract',
      nft: 'NFT',
      token: 'Token',
      'blockchain-infrastructure': 'Blockchain Infrastructure',
      'web3-integration': 'Web3 Integration',
      other: 'Other',
    }

    return displayNames[type] || type
  }

  // Get icon for project type
  const getTypeIcon = (type: string): JSX.Element => {
    switch (type) {
      case 'dapp':
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
            />
          </svg>
        )
      case 'smart-contract':
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        )
      case 'nft':
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        )
      case 'token':
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        )
      default:
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
        )
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mb-8 bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md"
    >
      <div className="flex flex-col sm:flex-row items-center justify-between mb-6">
        <h3 className="text-lg font-bold mb-4 sm:mb-0">Filter by Project Type</h3>
      </div>

      <div className="flex flex-wrap gap-3">
        {projectTypes.map(type => (
          <motion.button
            key={type}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleTypeChange(type)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleTypeChange(type)
              }
            }}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
              type === selectedType
                ? 'bg-primary text-white shadow-md'
                : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
            aria-pressed={type === selectedType}
            aria-label={`Filter by ${getDisplayName(type)} projects${type === selectedType ? ' (currently active)' : ''}`}
            role="button"
            tabIndex={0}
          >
            <span aria-hidden="true">{getTypeIcon(type)}</span>
            {getDisplayName(type)}
          </motion.button>
        ))}
      </div>
    </motion.div>
  )
}

export default ProjectFilters
